import { useEffect, useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import { generateCSV } from "utils/csv";
import { downloadFile } from "utils/downloads";
import { removeDuplicatedElements } from "utils/Array";
import {
  useAddAcademicYearMutation,
  useAddAcademicYearRequirementMutation,
  useGetAcademicYearsByOrgLazyQuery,
  useGetOrganizationsLazyQuery,
  useGetOrgRequirementsByOrgLazyQuery,
  useGetProgramsByOrgLazyQuery,
} from "graphql/__generated__/hooks";
import { Organization } from "types/organization";
import { Requirement } from "types/requirement";

const COLUMNS = ["name", "start_date", "end_date", "requirements"];

type ProgramData = {
  row_key: string;
  name: string;
  start_date: string;
  end_date: string;
  requirements: string;
};

type LoadingData = { current: number; total: number };

type ReportData = {
  success: string[];
  error: string[];
  alreadyExists: string[];
  missingRequirements: string[];
};

export const useUploadCSVPrograms = () => {
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [displayData, setDisplayData] = useState(false);
  const [data, setData] = useState<ProgramData[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState<LoadingData>({
    current: 0,
    total: 0,
  });
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [report, setReport] = useState<ReportData>({
    success: [],
    error: [],
    alreadyExists: [],
    missingRequirements: [],
  });
  const selectedData = useRef<string[]>([]);
  const navigate = useNavigate();

  const [getOrganizations, { data: organizationsData }] =
    useGetOrganizationsLazyQuery();
  const [getProgramsByOrg, { data: programsByOrgData }] =
    useGetProgramsByOrgLazyQuery();
  const [getOrgRequirementsByOrg, { data: orgRequirementsData }] =
    useGetOrgRequirementsByOrgLazyQuery();
  const [getAcademicYearsByOrg, { data: academicYearsByOrgData }] =
    useGetAcademicYearsByOrgLazyQuery();
  const [addAcademicYear] = useAddAcademicYearMutation();
  const [addAcademicYearRequirement] = useAddAcademicYearRequirementMutation();

  useEffect(() => {
    getOrganizations();
  }, []);

  useEffect(() => {
    if (!selectedOrganization?.org_id) return;
    getProgramsByOrg({ variables: { orgId: selectedOrganization.org_id } });
    getOrgRequirementsByOrg({ variables: { orgId: selectedOrganization.org_id } });
    getAcademicYearsByOrg({ variables: { orgId: selectedOrganization.org_id } });
  }, [selectedOrganization?.org_id]);

  const organizations = useMemo<Organization[]>(() => {
    return (organizationsData?.organization as Organization[]) || [];
  }, [organizationsData]);

  const selectedProgramId = useMemo(() => {
    return programsByOrgData?.program?.[0]?.program_id || "";
  }, [programsByOrgData]);

  const requirementIdByName = useMemo<Record<string, string>>(() => {
    const requirements =
      (orgRequirementsData?.organization_requirement?.map(
        (row) => row.requirement as Requirement,
      ) as Requirement[]) || [];
    return requirements.reduce(
      (acc, requirement) => {
        if (requirement.name && requirement.requirement_id) {
          acc[requirement.name.trim().toLowerCase()] = requirement.requirement_id;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [orgRequirementsData]);

  const existingAcademicYearKeys = useMemo(() => {
    const years =
      academicYearsByOrgData?.program?.map((program) => program.academic_years).flat() ||
      [];
    return new Set(
      years.map((year) => {
        const safeName = year.name?.trim().toLowerCase() || "";
        return `${safeName}|${year.start_date || ""}|${year.end_date || ""}`;
      }),
    );
  }, [academicYearsByOrgData]);

  const handleContinue = (uploadedFile: File) => {
    if (!uploadedFile) return;
    const reader = new FileReader();

    reader.onload = (event) => {
      if (!event.target?.result) return;
      const csvData = event.target.result as string;

      Papa.parse(csvData, {
        header: true,
        complete: (results: any) => {
          const parsedData = (results.data || [])
            .filter((item: ProgramData) => item.name)
            .map((item: ProgramData) => ({
              row_key: `${item.name}-${item.start_date}-${item.end_date}`,
              name: item.name?.trim() || "",
              start_date: item.start_date?.trim() || "",
              end_date: item.end_date?.trim() || "",
              requirements: item.requirements?.trim() || "",
            }));

          const parsedDataFiltered = removeDuplicatedElements(parsedData, "row_key");
          setData(parsedDataFiltered);
          selectedData.current = parsedDataFiltered.map((item) => item.row_key);
          setDisplayData(true);
        },
      });
    };

    reader.readAsText(uploadedFile);
  };

  const onSubmit = async () => {
    setError(null);
    if (!selectedOrganization?.org_id) {
      setError("Please select an organization");
      return;
    }
    if (!selectedProgramId) {
      setError("No program found for selected organization");
      return;
    }

    setLoading(true);

    const selectedRows =
      selectedData.current.length > 0
        ? data.filter((row) => selectedData.current.includes(row.row_key))
        : data;

    const success: string[] = [];
    const errorRows: string[] = [];
    const alreadyExists: string[] = [];
    const missingRequirements: string[] = [];

    setLoadingData({ current: 0, total: selectedRows.length });

    for (const row of selectedRows) {
      const rowLabel = `${row.name} (${row.start_date} - ${row.end_date})`;

      if (!row.name || !row.start_date || !row.end_date) {
        errorRows.push(`${rowLabel} - missing required fields`);
        setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
        continue;
      }

      const academicYearKey = `${row.name.trim().toLowerCase()}|${row.start_date}|${row.end_date}`;
      if (existingAcademicYearKeys.has(academicYearKey)) {
        alreadyExists.push(rowLabel);
        setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
        continue;
      }

      try {
        const result = await addAcademicYear({
          variables: {
            name: row.name,
            start_date: row.start_date,
            end_date: row.end_date,
            program_id: selectedProgramId,
          },
        });

        const academicYearId =
          result.data?.insert_academic_year?.returning?.[0]?.academic_year_id;

        if (!academicYearId) {
          errorRows.push(`${rowLabel} - failed creating program`);
          setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
          continue;
        }

        const requirementNames = row.requirements
          .split(";")
          .map((value) => value.trim())
          .filter(Boolean);

        const requirementObjects = requirementNames.reduce(
          (acc, requirementName) => {
            const requirementId = requirementIdByName[requirementName.toLowerCase()];
            if (!requirementId) {
              missingRequirements.push(`${row.name}: ${requirementName}`);
              return acc;
            }

            acc.push({
              academic_year_id: academicYearId,
              requirement_id: requirementId,
            });
            return acc;
          },
          [] as { academic_year_id: string; requirement_id: string }[],
        );

        if (requirementObjects.length > 0) {
          await addAcademicYearRequirement({
            variables: {
              objects: requirementObjects,
            },
          });
        }

        success.push(rowLabel);
      } catch {
        errorRows.push(rowLabel);
      }

      setLoadingData((prev) => ({ ...prev, current: prev.current + 1 }));
    }

    setReport({
      success,
      error: errorRows,
      alreadyExists,
      missingRequirements,
    });
    setLoading(false);
    setCompleted(true);
  };

  const handleDownloadClick = () => {
    const csvContent = generateCSV(
      [
        {
          name: "AY 2026",
          start_date: "2026-01-01",
          end_date: "2026-12-31",
          requirements: "EPA 1;EPA 2;EPA 3",
        },
      ],
      COLUMNS,
    );
    downloadFile(csvContent, "program_example.csv", "text/csv;charset=utf-8;");
  };

  const handleOnComplete = () => {
    navigate(AdminRoutes.AcademicYearList);
  };

  return {
    loading,
    completed,
    displayData,
    data,
    file,
    error,
    loadingData,
    report,
    organizations,
    selectedOrganization,
    selectedData,
    setSelectedOrganization,
    setDisplayData,
    setFile,
    handleContinue,
    handleDownloadClick,
    onSubmit,
    handleOnComplete,
  };
};
